import { useCallback, useEffect, useRef, useState } from "react";
import { monotonicNowMs, onForeground } from "@heraion/shared";
import { apiFetch, ApiError } from "./api";
import { onLateSession } from "./auth/cognito";

// The web console's profile read/write for Settings scalars (units, effort,
// sex, age, …). The API's PUT REMOVEs optional fields absent from the body
// (profiles.ts setIfPresent), so every save is a get-merge-PUT of the whole
// doc — the same round-trip mobile's patchProfile does. There's no offline
// cache here (the console is online); the mobile ProfileContext keeps its
// own AsyncStorage warm-cache.

type ProfileDoc = Record<string, unknown>;

export type UseProfileDoc = {
  doc: ProfileDoc | null;
  /** When the read behind `doc` left (client clock); null before one. */
  readStartedAt: number | null;
  loading: boolean;
  error: string | null;
  /** get-merge-PUT a single field, then refresh the local doc. Throws on failure. */
  saveField: (field: string, value: unknown) => Promise<void>;
  /** get-merge-PUT several fields at once (e.g. sex + seeded figure). Throws on failure. */
  savePatch: (patch: Record<string, unknown>) => Promise<void>;
  /** Re-pull the doc with no write (RW-18's LoggerHostDeps.refreshProfile —
   *  the MAX-bump / energy-readout paths need the freshest snapshot after a
   *  save, without a pointless get-merge-PUT-of-itself). */
  refresh: () => Promise<void>;
};

// PROF-1 — un 404 sulla GET del profilo vuol dire «documento VUOTO», non
// «errore». Chi entra su invito non ha ancora una riga PROFILE (misurato
// l'1/09: la GET risponde 404 `not_found`), e trattare quella risposta come un
// guasto rendeva impossibile la PRIMA scrittura — cioè esattamente quella che
// avrebbe creato la riga. La PUT lato API è create-or-update, quindi fondere
// sul vuoto è la cosa giusta.
//
// ⚠️ Questo NON basta da solo, e vale la pena scriverlo: la PUT valida
// `name`, che è l'unico campo dello schema senza default. Un patch che non lo
// porta, su un documento vuoto, prende un 400 invece di un 404 — meglio, ma
// non «funziona». È lo schermo di benvenuto (PROF-3) a garantire che il nome
// ci sia dal primo ingresso; questa è la rete sotto, non il pavimento.
const EMPTY_DOC: ProfileDoc = {};

const readDocOrEmpty = async (): Promise<ProfileDoc> => {
  try {
    return await apiFetch<ProfileDoc>("/api/profiles");
  } catch (err) {
    if ((err as ApiError).status === 404) return EMPTY_DOC;
    throw err;
  }
};

export function useProfileDoc(): UseProfileDoc {
  const [doc, setDoc] = useState<ProfileDoc | null>(null);
  const [readStartedAt, setReadStartedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The start of the newest read applied: an older one answering last must
  // not overwrite it (mobile's useAgeDeclared keeps the same order).
  const appliedRef = useRef(-Infinity);

  const load = useCallback(async () => {
    try {
      const startedAt = monotonicNowMs();
      const next = await readDocOrEmpty();
      // A tie loses: a read that left as a PUT answered may carry the doc
      // from before it.
      if (startedAt <= appliedRef.current) return;
      appliedRef.current = startedAt;
      // A re-read with nothing new keeps the doc's identity (#343): the
      // shell re-reads per route, and the screens' memos stay put.
      setDoc((prev) => (prev !== null && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      setReadStartedAt(startedAt);
      setError(null);
    } catch (err) {
      setError(`api_error_${(err as ApiError).status ?? "unknown"}`);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  // An offline boot read with no session: its session reads again (#175: the
  // shell's doc is what asks the age declaration).
  useEffect(() => onLateSession(() => void load()), [load]);
  // #180: the tab visible again (net.ts announces it) reads again, so an age
  // withdrawal made on another device arrives without a reload. Mobile's rule.
  useEffect(() => onForeground(() => void load()), [load]);

  const savePatch = useCallback(async (patch: Record<string, unknown>) => {
    const startedAt = monotonicNowMs();
    const current = await readDocOrEmpty();
    const next = { ...current, ...patch };
    await apiFetch<unknown>("/api/profiles", { method: "PUT", body: next });
    // #343: a write ranks from when its PUT answered. A read that left before
    // then may carry the doc from before the write (the old unit): landing
    // later, it must not overwrite this one.
    const writtenAt = monotonicNowMs();
    if (writtenAt < appliedRef.current) return;
    appliedRef.current = writtenAt;
    setDoc(next);
    setReadStartedAt(startedAt);
  }, []);

  const saveField = useCallback(
    (field: string, value: unknown) => savePatch({ [field]: value }),
    [savePatch],
  );

  return {
    doc,
    readStartedAt,
    loading: doc === null && error === null,
    error,
    saveField,
    savePatch,
    refresh: load,
  };
}
